/**
 * What: Strict canonical JSON serialization and SHA-256 fingerprints for stable identities.
 * Transport/data-flow: Pure functions; no I/O. Sorted keys at every depth; throws loudly on
 * values that would make identity unstable (undefined-valued keys, non-plain prototypes,
 * cycles, non-finite numbers, functions, symbols, bigints).
 *
 * Frozen exception: packages/runtime-analysis keeps its own byte-identical copy forever —
 * its output is the stored study-run canonical_model_hash compared on every execution;
 * changing this module must not change that one (see #3150, freeze decision 2026-09-22).
 */
export function canonicalJson(value) {
    return encode(value, new Set());
}
export async function sha256Fingerprint(value) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalJson(value)));
    return `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}
function encode(value, ancestors) {
    if (value === null || typeof value === 'boolean' || typeof value === 'string')
        return JSON.stringify(value);
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error('CANONICAL_JSON_NON_FINITE_NUMBER');
        return JSON.stringify(value);
    }
    if (typeof value === 'undefined' ||
        typeof value === 'function' ||
        typeof value === 'symbol' ||
        typeof value === 'bigint') {
        throw new Error('CANONICAL_JSON_UNSUPPORTED_VALUE');
    }
    if (Array.isArray(value)) {
        if (ancestors.has(value))
            throw new Error('CANONICAL_JSON_CYCLE');
        ancestors.add(value);
        const encoded = `[${value.map((entry) => encode(entry, ancestors)).join(',')}]`;
        ancestors.delete(value);
        return encoded;
    }
    if (typeof value === 'object') {
        const prototype = Object.getPrototypeOf(value);
        if (prototype !== Object.prototype && prototype !== null)
            throw new Error('CANONICAL_JSON_UNSUPPORTED_VALUE');
        if (ancestors.has(value))
            throw new Error('CANONICAL_JSON_CYCLE');
        ancestors.add(value);
        const record = value;
        const encoded = `{${Object.keys(record)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${encode(record[key], ancestors)}`)
            .join(',')}}`;
        ancestors.delete(value);
        return encoded;
    }
    throw new Error('CANONICAL_JSON_UNSUPPORTED_VALUE');
}
//# sourceMappingURL=canonical-json.js.map